import { addToast } from "@caprikorn/dynamo-ui";
import type { FetchiosError, FetchiosResponse } from "@caprikorn/dynamo-utils";
import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/AppQueryClient";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { apiEcmsHttp } from "../http";
import { QUERY_KEYS } from "../keys";
import type { PostUsers, UserWithAuthorizations } from "./apiEcms.types";

const postUsers = async (userData: PostUsers) => {
  const { data } = await apiEcmsHttp.post<
    PostUsers,
    FetchiosResponse<UserWithAuthorizations>
  >("/users", userData);
  return data;
};

export const usePostUsers = () => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation(["main", "common"]);

  return useMutation<UserWithAuthorizations, FetchiosError, PostUsers>({
    mutationFn: postUsers,
    mutationKey: [QUERY_KEYS.USER_CREATION, currentGroup],
    onSuccess: (user: UserWithAuthorizations) => {
      if (user.id === undefined) {
        throw new Error();
      }
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USERS, currentGroup],
      });
    },
    onError: (error) => {
      if (error.status === 403 || error.status === 400) {
        addToast({
          variant: "error",
          title: t("userCreate.error.title"),
          description: t("userCreate.error.description"),
        });
        return;
      }
      if (error.status === 404) {
        addToast({
          variant: "error",
          title: t("errors.notFound.title", { ns: "common" }),
        });
        return;
      }
      addToast({
        variant: "error",
        title: t("errors.title.large", { ns: "common" }),
      });
    },
  });
};
